import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip,
  Stack,
  Paper,
  useTheme,
  alpha,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  LocationOn,
  CheckCircle,
  PhotoLibrary,
  Close
} from '@mui/icons-material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { keyframes} from '@emotion/react';
import goalogo from 'assets/images/pictures/goa-logo.png';

export default function Wimssday() {
  const theme = useTheme();
  const [selectedImage, setSelectedImage] = React.useState(null);

  // Generate image paths
  const images = Array.from({ length: 10 }, (_, i) => ({
    id: i + 1,
    path: `/wimssday/wimssday_${String(i + 1).padStart(2, '0')}.jpg`,
    alt: `WIMSS Day 2026 - Photo ${i + 1}`
  }));

  const blink=keyframes`
    0% { opacity: 1;}
    50% { opacity: 0;}
    100% (opacity: 1;})  
  `;

  const handleImageClick = (imagePath) => {
    setSelectedImage(imagePath);
  };

  const handleCloseDialog = () => {
    setSelectedImage(null);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        py: { xs: 4, md: 6 },
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip
            label="EVENT COMPLETED ✓"
            color="success"
            sx={{ 
              fontWeight: 600, 
              mb: 2,
              bgcolor: alpha(theme.palette.success.main, 0.1),
              color: theme.palette.success.dark
            }}
          />
          <Typography 
            variant="h2" 
            component="h1"
            sx={{ 
              fontWeight: 700, 
              color: theme.palette.primary.dark,
              mb: 1,
              fontSize: { xs: '1.75rem', md: '2.5rem' }
            }}
          >
            WIMSS Day 2026
          </Typography>
          <Typography 
            variant="h5" 
            color="primary"
            sx={{ 
              fontWeight: 500, 
              mb: 3,
              fontStyle: 'italic'
            }}
          >
            A Day of Connecting, Exercising, Recovering
          </Typography>
          
          {/* Event Details Bar */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              bgcolor: 'white', 
              borderRadius: 2,
              display: 'inline-flex',
              flexWrap: 'wrap',
              gap: 2,
              justifyContent: 'center',
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarToday color="primary" fontSize="small" />
              <Typography variant="body2" fontWeight={500}>September 5, 2026</Typography>
            </Stack>
            <Divider orientation="vertical" flexItem />
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTime color="primary" fontSize="small" />
              <Typography variant="body2" fontWeight={500}>9 AM - 3 PM</Typography>
            </Stack>
            <Divider orientation="vertical" flexItem />
            <Stack direction="row" spacing={1} alignItems="center">
              <LocationOn color="primary" fontSize="small" />
              <Typography variant="body2" fontWeight={500}>Site #4, Hawrelak Park, Edmonton</Typography>
            </Stack>
          </Paper>
        </Box>

        {/* Success Banner */}
        <Paper
          elevation={0}
          sx={{
            mb: 6,
            p: 3,
            bgcolor: alpha(theme.palette.success.main, 0.08),
            borderRadius: 2,
            border: `1px dashed ${theme.palette.success.main}`,
            textAlign: 'center'
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
            <Box component="img" src={goalogo} alt="GoA Logo" sx={{ width: 80, height: 'auto'}}/>
            <Typography variant="h6" fontWeight={600} sx={{ animation: `${blink} 1s infinite`, color: "primary.main"}}>
              A heartfelt thank you to the Government of Alberta for their generous funding and support in making this event possible!
            </Typography>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            mb: 6,
            p: 3,
            bgcolor: alpha(theme.palette.success.main, 0.08),
            borderRadius: 2,
            border: `1px dashed ${theme.palette.success.main}`,
            textAlign: 'center'
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
            <CheckCircle color="success" sx={{ fontSize: 32 }} />
            <Typography variant="h6" fontWeight={600} color="success.dark">
              Thank you to all 150+ participants, 2 hiking coaches, and 40+ volunteers!
            </Typography>
          </Stack>
        </Paper>

        {/* Article Section */}
        <Card 
          elevation={0} 
          sx={{ 
            mb: 6, 
            bgcolor: 'white',
            border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography 
              variant="h4" 
              fontWeight={700} 
              gutterBottom 
              color="primary.dark"
              sx={{ mb: 3, lineHeight: 1.3 }}
            >
              WIMSS Day 2026 Hiking Event a Resounding Success: Community Unites for Health and Nature in the River Valley
            </Typography>
            
            <Typography 
              variant="subtitle1" 
              color="text.secondary" 
              sx={{ mb: 3, fontStyle: 'italic' }}
            >
              EDMONTON, AB — September 8, 2026
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3, textAlign: 'left'}}>
              On September 5, 2026, Hawrelak Park transformed into a vibrant hub of intercultural connection and outdoor enthusiasm as the highly anticipated event outlined in the WIMSS Day 2026 Hiking Event Plan successfully unfolded. Under absolute perfect hiking conditions—a cool, cloudy 15 degrees Celsius with completely dry skies—over 150 passionate hiking lovers gathered in the scenic North Saskatchewan River Valley to embrace the themes of connecting, exercising, and recovering.
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3, textAlign: 'left'}}>
              The day flawlessly executed its primary mission of building a cohesive community while promoting a healthy, active lifestyle. Participants eagerly hit the trails, forging great new relationships and supporting the local environment by diligently picking up garbage along the routes. Beyond the planned team hiking and casual competitions, the vibrant crowd brought incredible energy to the park by engaging in fun supplementary activities, including playing lively games of volleyball and going mushroom picking in the surrounding woods.
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3, textAlign: 'left'}}>
              Following the morning excursions, hikers reconvened for a well-deserved post-hike pizza gathering to solidify their new friendships. Supported by the expertise of two dedicated hiking coaches and an incredible team of 40+ volunteers, the event comprehensively achieved all of its planning objectives. From celebrating Alberta's natural landscapes to promoting vital physical and mental well-being, this WIMSS Day proved to be a monumental success, leaving a lasting, positive impact on Edmonton's multicultural outdoor recreation community.
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3, textAlign: 'left'}}>
              One of our hiking participants sent us a great feedback after the event:
            </Typography>

            <Typography variant="body1" component="blockquote" color="text.secondary" sx={{ lineHeight: 1.8, textAlign: 'left', fontStyle: 'italic'}}>
              "What an amazing experience! The WIMSS Day 2026 hiking event was such a wonderful opportunity to enjoy the beautiful river valley, get active, and connect with so many great people. The weather was a little cooler and the sun stayed away, but it actually made the hike comfortable and refreshing. Everything was well organized, and the hiking coaches were friendly, encouraging, and incredibly supportive throughout the day. I really appreciated the positive energy and welcoming atmosphere. A huge thank you to the WIMSS team, volunteers, coaches, and sponsors for making this memorable day possible. I can’t wait for the next one!"
            </Typography>
          </CardContent>
        </Card>

        {/* Photo Gallery Section */}
        <Typography 
          variant="h4" 
          fontWeight={700} 
          align="center" 
          gutterBottom 
          sx={{ mb: 1, color: theme.palette.primary.dark }}
        >
          Event Photo Gallery
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          align="center" 
          sx={{ mb: 4 }}
        >
          Click on any photo to view full size
        </Typography>

        <Grid container spacing={2} sx={{ mb: 6 }}>
          {images.map((image) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
              <Card 
                elevation={0}
                sx={{
                  height: 280,
                  overflow: 'hidden',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
                onClick={() => handleImageClick(image.path)}
              >
                <Box
                  component="img"
                  src={image.path}
                  alt={image.alt}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Footer */}
        <Divider sx={{ my: 4 }} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            🌿 WIMSS Day 2026 • Alberta Community Hiking Festival • Hawrelak Park, Edmonton
          </Typography>
          {/* <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
            © 2026 WIMSS. All rights reserved.
          </Typography> */}
        </Box>
      </Container>

      {/* Full Size Image Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            maxWidth: '95vw'
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Full Size Photo
          <IconButton 
            onClick={handleCloseDialog} 
            size="small"
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, textAlign: 'center' }}>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Full size"
              sx={{
                width: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}